import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import type { Model } from '@khadi/shared';
import { api, ApiError } from '../lib/api';
import type { NamedEntity } from '../lib/queries';

/**
 * Neues Modell direkt aus der Erfassung heraus anlegen —
 * ohne den laufenden Vorgang zu verlassen.
 */
export function ModelCreateDialog({
  categories,
  manufacturers,
  defaultName,
  onClose,
  onCreated,
}: {
  categories: NamedEntity[];
  manufacturers: NamedEntity[];
  defaultName?: string;
  onClose: () => void;
  onCreated: (model: Model) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(defaultName ?? '');
  const [categoryId, setCategoryId] = useState<number>(categories[0]?.id ?? 0);
  const [manufacturerId, setManufacturerId] = useState<number>(0);
  const [modelNumber, setModelNumber] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api<Model>('/models', {
        method: 'POST',
        body: {
          name: name.trim(),
          category_id: categoryId,
          manufacturer_id: manufacturerId || null,
          model_number: modelNumber.trim() || null,
        },
      }),
    onSuccess: (model) => {
      void queryClient.invalidateQueries({ queryKey: ['models'] });
      toast.success(`Modell „${model.name}" angelegt`);
      onCreated(model);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Das Modell konnte nicht angelegt werden'),
  });

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-[var(--color-khadi-forest-900)]/45 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Modell anlegen"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="animate-sheet-up safe-bottom w-full max-w-md rounded-t-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-bg-elevated)] p-5 shadow-[var(--shadow-lg)] sm:rounded-[var(--radius-xl)]">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-[family-name:var(--font-display)] text-2xl">Modell anlegen</h2>
          <button type="button" onClick={onClose} className="btn btn-quiet btn-sm" aria-label="Schließen">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <div>
            <label className="label" htmlFor="model-name">
              Bezeichnung
            </label>
            <input
              id="model-name"
              autoFocus
              className="field"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder='z. B. MacBook Pro 16" M4'
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="model-category">
                Kategorie
              </label>
              <select
                id="model-category"
                className="field"
                value={categoryId}
                onChange={(event) => setCategoryId(Number(event.target.value))}
                required
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="model-manufacturer">
                Hersteller
              </label>
              <select
                id="model-manufacturer"
                className="field"
                value={manufacturerId}
                onChange={(event) => setManufacturerId(Number(event.target.value))}
              >
                <option value={0}>— offen —</option>
                {manufacturers.map((manufacturer) => (
                  <option key={manufacturer.id} value={manufacturer.id}>
                    {manufacturer.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label" htmlFor="model-number">
              Modellnummer <span className="font-normal normal-case">(optional)</span>
            </label>
            <input
              id="model-number"
              className="field tabular"
              value={modelNumber}
              onChange={(event) => setModelNumber(event.target.value)}
              placeholder="z. B. A2992"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg w-full"
            disabled={create.isPending || !name.trim() || !categoryId}
          >
            {create.isPending ? 'Wird angelegt …' : 'Modell anlegen'}
          </button>
        </form>
      </div>
    </div>
  );
}
